import { useState, useEffect } from 'react';
import Modal from '@/components/base/Modal';
import { classSections, classTeacherMap, type StudentRecord, type StudentDocument } from '@/mocks/students';

interface StudentDetailModalProps {
  open: boolean;
  student: StudentRecord | null;
  onClose: () => void;
  onSave: (updated: StudentRecord) => void;
}

const statusOptions: { value: StudentRecord['status']; label: string }[] = [
  { value: 'enrolled', label: 'Enrolled' },
  { value: 'pending', label: 'Pending' },
  { value: 'draft', label: 'Draft' },
  { value: 'inactive', label: 'Inactive' },
];

export default function StudentDetailModal({
  open,
  student,
  onClose,
  onSave,
}: StudentDetailModalProps) {
  const [formData, setFormData] = useState<Partial<StudentRecord>>({});
  const [previewDoc, setPreviewDoc] = useState<StudentDocument | null>(null);

  useEffect(() => {
    if (student) {
      setFormData({ ...student });
    }
  }, [student, open]);

  const updateField = (field: string, value: string) => {
    const updates: Partial<StudentRecord> = { [field]: value };
    if (field === 'className') {
      updates.classTeacher = classTeacherMap[value] || '';
    }
    setFormData((prev) => ({ ...prev, ...updates }));
  };

  const handleSave = () => {
    if (!student || !formData.name || !formData.className) return;
    const newStatus = (formData.status as StudentRecord['status']) || student.status;
    const wasInactive = student.status === 'inactive';
    const isNowInactive = newStatus === 'inactive';
    const updated: StudentRecord = {
      ...student,
      name: formData.name,
      dob: formData.dob || student.dob,
      address: formData.address || student.address,
      aadhaarNumber: formData.aadhaarNumber || student.aadhaarNumber,
      udidNumber: formData.udidNumber || student.udidNumber,
      className: formData.className,
      classTeacher: formData.classTeacher || classTeacherMap[formData.className] || student.classTeacher,
      fatherName: formData.fatherName || student.fatherName,
      motherName: formData.motherName || student.motherName,
      contactNumberF: formData.contactNumberF || student.contactNumberF,
      contactNumberM: formData.contactNumberM || student.contactNumberM,
      email: formData.email || student.email,
      district: formData.district || student.district,
      town: formData.town || student.town,
      status: newStatus,
      deactivatedAt: isNowInactive
        ? (student.deactivatedAt || new Date().toISOString().split('T')[0])
        : (wasInactive && !isNowInactive ? undefined : student.deactivatedAt),
    };
    onSave(updated);
    onClose();
  };

  if (!student) return null;

  const currentStatus = (formData.status || student.status) as StudentRecord['status'];
  const statusLabels: Record<string, string> = {
    enrolled: 'Enrolled',
    pending: 'Pending',
    draft: 'Draft',
    inactive: 'Inactive',
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Edit Student"
      subtitle={student.studentId}
      size="xl"
      footer={
        <div className="flex items-center justify-between w-full">
          <div className="flex items-center gap-2">
            <span className={`inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-xs font-label font-medium ${
              currentStatus === 'enrolled'
                ? 'bg-semantic-success-bg text-semantic-success'
                : currentStatus === 'pending'
                  ? 'bg-semantic-warning-bg text-semantic-warning'
                  : currentStatus === 'inactive'
                    ? 'bg-semantic-error-bg text-semantic-error'
                    : 'bg-foreground-100 text-foreground-500'
            }`}>
              <span className={`w-1.5 h-1.5 rounded-full ${
                currentStatus === 'enrolled'
                  ? 'bg-semantic-success'
                  : currentStatus === 'pending'
                    ? 'bg-semantic-warning'
                    : currentStatus === 'inactive'
                      ? 'bg-semantic-error'
                      : 'bg-foreground-400'
              }`} />
              {statusLabels[currentStatus]}
            </span>
          </div>
          <div className="flex items-center gap-3">
            {currentStatus !== 'inactive' && (
              <button
                type="button"
                onClick={() => {
                  const updated: StudentRecord = {
                    ...student,
                    name: formData.name || student.name,
                    dob: formData.dob || student.dob,
                    address: formData.address || student.address,
                    aadhaarNumber: formData.aadhaarNumber || student.aadhaarNumber,
                    udidNumber: formData.udidNumber || student.udidNumber,
                    className: formData.className || student.className,
                    classTeacher: formData.classTeacher || classTeacherMap[formData.className || student.className] || student.classTeacher,
                    fatherName: formData.fatherName || student.fatherName,
                    motherName: formData.motherName || student.motherName,
                    contactNumberF: formData.contactNumberF || student.contactNumberF,
                    contactNumberM: formData.contactNumberM || student.contactNumberM,
                    email: formData.email || student.email,
                    district: formData.district || student.district,
                    town: formData.town || student.town,
                    status: 'inactive',
                    deactivatedAt: student.deactivatedAt || new Date().toISOString().split('T')[0],
                  };
                  onSave(updated);
                  onClose();
                }}
                className="inline-flex items-center gap-2 h-10 px-4 bg-semantic-error-bg text-semantic-error text-sm font-label font-medium rounded-lg hover:bg-semantic-error/20 transition-colors cursor-pointer whitespace-nowrap border border-semantic-error/30"
              >
                <i className="ri-user-unfollow-line" />
                Deactivate
              </button>
            )}
            <button
              onClick={onClose}
              className="inline-flex items-center gap-2 h-10 px-4 bg-background-100 text-foreground-600 text-sm font-label font-medium rounded-lg hover:bg-background-200 transition-colors cursor-pointer whitespace-nowrap"
            >
              Cancel
            </button>
            <button
              onClick={handleSave}
              className="inline-flex items-center gap-2 h-10 px-5 bg-primary-500 text-background-50 text-sm font-label font-medium rounded-lg hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
            >
              <i className="ri-save-line" />
              Save Changes
            </button>
          </div>
        </div>
      }
    >
      <div className="space-y-6">
        {/* ── Demographics ─────────────────────────────────── */}
        <div>
          <h3 className="text-sm font-label font-semibold text-foreground-700 uppercase tracking-wide mb-4 flex items-center gap-2">
            <i className="ri-user-line text-foreground-500" />
            Student Demographics
          </h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="flex flex-col gap-1.5">
              <label className="text-sm font-medium text-foreground-700 font-label">Full Name</label>
              <input
                type="text"
                value={formData.name || ''}
                onChange={(e) => updateField('name', e.target.value)}
                className="xl-input"
              />
            </div>
            <div className="flex flex-col gap-1.5">
              <label className="text-sm font-medium text-foreground-700 font-label">Date of Birth</label>
              <input
                type="date"
                value={formData.dob || ''}
                onChange={(e) => updateField('dob', e.target.value)}
                className="xl-input"
              />
            </div>
            <div className="flex flex-col gap-1.5">
              <label className="text-sm font-medium text-foreground-700 font-label">Class / Section</label>
              <select
                value={formData.className || ''}
                onChange={(e) => updateField('className', e.target.value)}
                className="xl-input cursor-pointer"
              >
                {classSections.map((c) => (
                  <option key={c} value={c}>{c}</option>
                ))}
              </select>
            </div>
            <div className="flex flex-col gap-1.5">
              <label className="text-sm font-medium text-foreground-700 font-label">Assigned Teacher</label>
              <div className="xl-input bg-background-100 text-foreground-500 flex items-center gap-2 select-none">
                <i className="ri-user-star-line text-base text-foreground-400" />
                <span>{formData.classTeacher || classTeacherMap[formData.className || ''] || '—'}</span>
              </div>
            </div>
            <div className="flex flex-col gap-1.5">
              <label className="text-sm font-medium text-foreground-700 font-label">Aadhaar Number</label>
              <input
                type="text"
                value={formData.aadhaarNumber || ''}
                onChange={(e) => updateField('aadhaarNumber', e.target.value)}
                className="xl-input"
              />
            </div>
            <div className="flex flex-col gap-1.5">
              <label className="text-sm font-medium text-foreground-700 font-label">UDID Number</label>
              <input
                type="text"
                value={formData.udidNumber || ''}
                onChange={(e) => updateField('udidNumber', e.target.value)}
                className="xl-input"
              />
            </div>
            <div className="flex flex-col gap-1.5">
              <label className="text-sm font-medium text-foreground-700 font-label">Status</label>
              <select
                value={formData.status || student.status}
                onChange={(e) => updateField('status', e.target.value)}
                className="xl-input cursor-pointer"
              >
                {statusOptions.map((s) => (
                  <option key={s.value} value={s.value}>{s.label}</option>
                ))}
              </select>
            </div>
            <div className="flex flex-col gap-1.5 sm:col-span-2">
              <label className="text-sm font-medium text-foreground-700 font-label">Address</label>
              <input
                type="text"
                value={formData.address || ''}
                onChange={(e) => updateField('address', e.target.value)}
                className="xl-input"
              />
            </div>
          </div>
        </div>

        {/* ── Guardian Details ─────────────────────────────── */}
        <div className="border-t border-background-200 pt-6">
          <h3 className="text-sm font-label font-semibold text-foreground-700 uppercase tracking-wide mb-4 flex items-center gap-2">
            <i className="ri-parent-line text-foreground-500" />
            Guardian Information
          </h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="flex flex-col gap-1.5">
              <label className="text-sm font-medium text-foreground-700 font-label">Father Name</label>
              <input
                type="text"
                value={formData.fatherName || ''}
                onChange={(e) => updateField('fatherName', e.target.value)}
                className="xl-input"
              />
            </div>
            <div className="flex flex-col gap-1.5">
              <label className="text-sm font-medium text-foreground-700 font-label">Mother Name</label>
              <input
                type="text"
                value={formData.motherName || ''}
                onChange={(e) => updateField('motherName', e.target.value)}
                className="xl-input"
              />
            </div>
            <div className="flex flex-col gap-1.5">
              <label className="text-sm font-medium text-foreground-700 font-label">Contact (Father)</label>
              <input
                type="text"
                value={formData.contactNumberF || ''}
                onChange={(e) => updateField('contactNumberF', e.target.value)}
                className="xl-input"
              />
            </div>
            <div className="flex flex-col gap-1.5">
              <label className="text-sm font-medium text-foreground-700 font-label">Contact (Mother)</label>
              <input
                type="text"
                value={formData.contactNumberM || ''}
                onChange={(e) => updateField('contactNumberM', e.target.value)}
                className="xl-input"
              />
            </div>
            <div className="flex flex-col gap-1.5">
              <label className="text-sm font-medium text-foreground-700 font-label">Email Address</label>
              <input
                type="email"
                value={formData.email || ''}
                onChange={(e) => updateField('email', e.target.value)}
                className="xl-input"
              />
            </div>
            <div className="flex flex-col gap-1.5">
              <label className="text-sm font-medium text-foreground-700 font-label">District</label>
              <input
                type="text"
                value={formData.district || ''}
                onChange={(e) => updateField('district', e.target.value)}
                className="xl-input"
              />
            </div>
            <div className="flex flex-col gap-1.5">
              <label className="text-sm font-medium text-foreground-700 font-label">Town / City</label>
              <input
                type="text"
                value={formData.town || ''}
                onChange={(e) => updateField('town', e.target.value)}
                className="xl-input"
              />
            </div>
          </div>
        </div>

        {/* ── Documents ───────────────────────────────────── */}
        <div className="border-t border-background-200 pt-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-sm font-label font-semibold text-foreground-700 uppercase tracking-wide flex items-center gap-2">
              <i className="ri-folder-line text-foreground-500" />
              Documents
            </h3>
            {(() => {
              const docs = (formData.documents || student.documents) as StudentDocument[];
              const uploaded = docs.filter((d) => d.uploaded).length;
              const required = docs.filter((d) => d.required).length;
              const requiredUploaded = docs.filter((d) => d.required && d.uploaded).length;
              return (
                <span className={`inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-xs font-label font-medium ${
                  requiredUploaded === required
                    ? 'bg-semantic-success-bg text-semantic-success'
                    : 'bg-semantic-warning-bg text-semantic-warning'
                }`}>
                  <i className={`text-[10px] ${requiredUploaded === required ? 'ri-checkbox-circle-fill' : 'ri-error-warning-line'}`} />
                  {uploaded} of {docs.length} uploaded{requiredUploaded < required ? ` (${required - requiredUploaded} required missing)` : ''}
                </span>
              );
            })()}
          </div>

          {/* Preview Panel */}
          {previewDoc && (
            <div className="mb-4 p-4 bg-background-100 rounded-xl border border-background-200 animate-[modalEnter_150ms_cubic-bezier(0.4,0,0.2,1)]">
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2.5">
                  <div className="w-8 h-8 flex items-center justify-center rounded-lg bg-primary-100">
                    <i className="ri-file-pdf-line text-primary-600" />
                  </div>
                  <div>
                    <p className="text-sm font-label font-semibold text-foreground-800">{previewDoc.fileName}</p>
                    <p className="text-xs text-foreground-500">{previewDoc.name}</p>
                  </div>
                </div>
                <button
                  type="button"
                  onClick={() => setPreviewDoc(null)}
                  className="w-7 h-7 flex items-center justify-center rounded-full bg-background-200 text-foreground-500 hover:bg-background-300 transition-colors cursor-pointer"
                >
                  <i className="ri-close-line text-sm" />
                </button>
              </div>
              <div className="bg-background-50 rounded-lg border border-background-200 p-6 flex flex-col items-center justify-center min-h-[200px]">
                <div className="w-16 h-16 flex items-center justify-center rounded-xl bg-primary-50 mb-3">
                  <i className="ri-file-text-line text-2xl text-primary-500" />
                </div>
                <p className="text-sm font-label font-medium text-foreground-700 mb-1">Document Preview</p>
                <p className="text-xs text-foreground-400 mb-4 text-center max-w-xs">
                  This is a preview placeholder for <span className="font-semibold text-foreground-600">{previewDoc.fileName}</span>.
                  In production, the actual document would render here.
                </p>
                <div className="flex items-center gap-2">
                  <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-md bg-background-100 text-xs font-mono text-foreground-500">
                    <i className="ri-file-line text-sm" />
                    PDF Document
                  </span>
                  <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-md bg-background-100 text-xs font-mono text-foreground-500">
                    <i className="ri-check-line text-sm text-semantic-success" />
                    Verified
                  </span>
                </div>
              </div>
            </div>
          )}

          {/* Document List */}
          <div className="rounded-xl border border-background-200 overflow-hidden">
            <div className="max-h-[360px] overflow-y-auto">
              {((formData.documents || student.documents) as StudentDocument[]).map((doc, i) => (
                <div
                  key={doc.id}
                  className={`flex items-center gap-3 px-4 py-2.5 ${
                    i % 2 === 0 ? 'bg-background-50' : 'bg-background-50/50'
                  } ${i > 0 ? 'border-t border-background-100' : ''}`}
                >
                  {/* Icon */}
                  <div className={`w-8 h-8 flex items-center justify-center rounded-lg flex-shrink-0 ${
                    doc.uploaded ? 'bg-semantic-success-bg' : 'bg-foreground-100'
                  }`}>
                    <i className={`text-sm ${
                      doc.uploaded
                        ? 'ri-file-text-line text-semantic-success'
                        : 'ri-file-close-line text-foreground-400'
                    }`} />
                  </div>

                  {/* Name + Meta */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-label font-medium text-foreground-800 truncate">
                        {doc.name}
                      </span>
                      {doc.required && (
                        <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-label font-semibold bg-semantic-error-bg text-semantic-error flex-shrink-0">
                          Required
                        </span>
                      )}
                    </div>
                    {doc.uploaded && doc.fileName ? (
                      <p className="text-xs font-mono text-foreground-400 truncate mt-0.5">{doc.fileName}</p>
                    ) : (
                      <p className="text-xs text-semantic-error font-label mt-0.5">Not uploaded</p>
                    )}
                  </div>

                  {/* Actions */}
                  <div className="flex items-center gap-1 flex-shrink-0">
                    {doc.uploaded ? (
                      <>
                        <button
                          type="button"
                          onClick={() => setPreviewDoc(previewDoc?.id === doc.id ? null : doc)}
                          className={`w-8 h-8 flex items-center justify-center rounded-lg transition-colors cursor-pointer ${
                            previewDoc?.id === doc.id
                              ? 'bg-primary-500 text-background-50'
                              : 'bg-background-100 text-foreground-500 hover:bg-primary-100 hover:text-primary-600'
                          }`}
                          title={previewDoc?.id === doc.id ? 'Close preview' : 'Preview document'}
                        >
                          <i className={`text-xs ${previewDoc?.id === doc.id ? 'ri-eye-off-line' : 'ri-eye-line'}`} />
                        </button>
                        <button
                          type="button"
                          className="w-8 h-8 flex items-center justify-center rounded-lg bg-background-100 text-foreground-500 hover:bg-secondary-100 hover:text-secondary-600 transition-colors cursor-pointer"
                          title="Download document"
                        >
                          <i className="ri-download-line text-xs" />
                        </button>
                      </>
                    ) : (
                      <button
                        type="button"
                        className="inline-flex items-center gap-1.5 h-7 px-3 rounded-md bg-foreground-100 text-foreground-500 text-xs font-label font-medium hover:bg-foreground-200 transition-colors cursor-pointer"
                        title="Upload document"
                      >
                        <i className="ri-upload-line text-xs" />
                        Upload
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </Modal>
  );
}